import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import fetcher from '../../api/helpers/fetcher';
import { bookingRetrieveUrl } from '../../api/helpers/api-constants';
import ButtonPrimary from '../../components/ui/Button/ButtonPrimary';
import Input from '../../components/ui/Input/Input';
import Label from '../../components/ui/Label';
import { useTranslation } from 'react-i18next';
import { useReduxReducer } from 'redux/store';

// Utility function to normalize Turkish characters
const normalizeTurkishChars = (text: string): string => {
  const turkishCharMap: { [key: string]: string } = {
    'ğ': 'g', 'Ğ': 'G',
    'ü': 'u', 'Ü': 'U',
    'ş': 's', 'Ş': 'S',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ç': 'c', 'Ç': 'C'
  };

  return text.replace(/[ğĞüÜşŞıİöÖçÇ]/g, (match) => turkishCharMap[match] || match);
};

const ReservationSearch: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile } = useReduxReducer(state => state.general);
  const [surname, setSurname] = useState<string>('');
  const [pnr, setPnr] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  // Clear error when inputs change
  useEffect(() => {
    if (error) setError(null);
  }, [surname, pnr]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!surname || !pnr) {
      setError(t('flight.b2c.findreservation.error.nodetails'));
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetcher(bookingRetrieveUrl(pnr, ''), {
        method: 'POST',
      });

      if (response) {
        if (response.mergedPaxList && response.mergedPaxList.length > 0) {
          
          const passengerList = Array.isArray(response.mergedPaxList[0]) 
            ? response.mergedPaxList[0] 
            : [response.mergedPaxList[0]];
          
            const surnameMatches = passengerList.some(
            (passenger: any) => passenger.surname && passenger.surname.toLowerCase() === surname.toLowerCase()
          );
          
          if (surnameMatches) {
            setIsSuccess(true);
            setTimeout(() => {
              navigate(`/booking/detailb2c/${pnr}`);
            }, 100);
          } else {
            setError(t('flight.b2c.findreservation.error.noreservation'));
          }
        } else {
          setError(t('flight.b2c.findreservation.error.nopassengerinfo'));
        }
      } else {
        setError(t('flight.b2c.findreservation.error.noreservation'));
      }
    } catch (error) {
      console.error(error);
      setError(t('flight.b2c.findreservation.error.searcherror'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <div className="container mx-auto px-4 py-16">
        <div className={`max-w-lg mx-auto bg-white ${isMobile ? 'p-5' : 'p-8'} border border-gray-200 rounded-2xl shadow-lg`}>
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-center mb-2">{t("flight.b2c.findreservation")}</h2>
          <p className="text-center text-gray-500 mb-8">
            {t("flight.b2c.findreservation.description")}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-2">{t("flight.b2c.findreservation.surname")}</Label>
              <Input
                id="surname"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                type="text"
                placeholder={t("react.general.validationerrormsg.surname")}
                value={surname}
                onChange={(e) => setSurname(normalizeTurkishChars(e.target.value))}
              />
              <p className="text-xs text-gray-500 mt-1 ml-1">{t("flight.b2c.findreservation.surname.description")}</p>
            </div>
            
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-2">{t("flight.b2c.findreservation.bookingreference")}</Label>
              <Input
                id="pnr"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                type="text"
                placeholder={t("react.general.validationerrormsg.bookingreference")}
                value={pnr}
                onChange={(e) => setPnr(normalizeTurkishChars(e.target.value))}
              />
              <p className="text-xs text-gray-500 mt-1 ml-1">{t("flight.b2c.findreservation.bookingreference.description")}</p>
            </div>
            
            {error && (
              <div className="text-red-500 text-sm p-4 bg-red-50 rounded-xl border border-red-100 flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}
            
            <ButtonPrimary 
              className="w-full py-4 rounded-xl"
              type="submit"
              disabled={loading || isSuccess}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Searching...
                </div>
              ) : isSuccess ? (
                <div className="flex items-center justify-center">
                  <svg className="mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Reservation Found!
                </div>
              ) : (
                t("flight.b2c.findreservation")
              )}
            </ButtonPrimary>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex justify-center space-x-6">
              <button 
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => navigate('/aboutus')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t("flight.b2c.findreservation.contactsupport")}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto mt-6">
          <div className="bg-blue-50 p-4 rounded-xl">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <p className="text-sm text-blue-700">
                  {t("flight.b2c.findreservation.cantfindbooking")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationSearch; 